/**
 * Modèles SEO partagés pour les pages de collection.
 * Un seul endroit pour les meta titles/descriptions, OG/Twitter, canonical
 * et les données structurées CollectionPage + fil d'Ariane.
 */

export const SITE_URL = "https://sunsetconcept.store";
export const SITE_NAME = "Sunset Concept Store";

type CategorySeo = {
  /** Chemin de la page, ex. "/beachwear". */
  path: string;
  /** Meta title (< 60 caractères conseillé). */
  title: string;
  /** Meta description (< 160 caractères). */
  description: string;
  /** Description sociale, reprend la description si absente. */
  socialDescription?: string;
  /** Nom lisible utilisé dans le fil d'Ariane et le CollectionPage. */
  name: string;
  /** Visuel de collection (chemin relatif servi depuis /public). */
  image?: string;
  /** Texte alternatif du visuel de partage. */
  imageAlt?: string;
  /** Produits affichés dans la grille, utilisés pour le JSON-LD ItemList. */
  products?: Array<{ slug: string; name: string; price: number; image?: string }>;
};

/** Construit un JSON-LD ItemList à partir des produits d'une collection. */
function buildItemList(
  name: string,
  url: string,
  products: Array<{ slug: string; name: string; price: number; image?: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `Produits — ${name}`,
    url,
    numberOfItems: products.length,
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    itemListElement: products.map((product, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Product",
        name: product.name,
        url: `${SITE_URL}/produit/${product.slug}`,
        ...(product.image
          ? { image: product.image.startsWith("http") ? product.image : `${SITE_URL}${product.image}` }
          : {}),
        offers: {
          "@type": "Offer",
          price: product.price.toFixed(2),
          priceCurrency: "MAD",
          availability: "https://schema.org/InStock",
          url: `${SITE_URL}/produit/${product.slug}`,
        },
      },
    })),
  };
}

/** Construit l'objet `head()` complet d'une page de collection. */
export function categoryHead({
  path,
  title,
  description,
  socialDescription,
  name,
  image,
  imageAlt,
  products,
}: CategorySeo) {
  const url = `${SITE_URL}${path}`;
  const social = socialDescription ?? description;
  const imageUrl = image ? `${SITE_URL}${image}` : null;

  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: social },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:locale", content: "fr_FR" },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: social },
      ...(imageUrl
        ? [
            { property: "og:image", content: imageUrl },
            { property: "og:image:alt", content: imageAlt ?? name },
            { name: "twitter:image", content: imageUrl },
            { name: "twitter:image:alt", content: imageAlt ?? name },
          ]
        : []),
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name,
          description,
          url,
          ...(imageUrl ? { image: imageUrl } : {}),
          isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: SITE_URL },
              { "@type": "ListItem", position: 2, name, item: url },
            ],
          },
        }),
      },
      ...(products && products.length > 0
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify(buildItemList(name, url, products)),
            },
          ]
        : []),
    ],
  };
}
